// Service stamp / signature fetching from the HRMS API.
//
// The registrar's completion-letter and amended-research files are stamped by
// the research backend. The backend expects up to two signature images:
//   signature1 -> bottom-center
//   signature2 -> bottom-right
// Both -> two-signature stamp, one -> single centered stamp, none -> unstamped.
//
// This module fetches the configured service stamp from HRMS and converts the
// returned signature image(s) into File objects ready to attach to the
// multipart request to /legal_research/legal-research-requests/.

import { jwtDecode } from "jwt-decode";

const HRMS_BASE = process.env.REACT_APP_API_BASE_URL_2;

// HRMS registers research's stamp under the service "rldif" (RDLIF), one per
// office; the registrar's own office (admin_unit in the login token) is used.
export const RESEARCH_STAMP_SERVICE_NAME = "rldif";

/** The login token (stored raw or as JSON) and the office in it. */
export const currentLogin = (): { token: string; adminUnit?: string } => {
  let token = "";
  try {
    const raw = sessionStorage.getItem("authToken") || localStorage.getItem("authToken") || "";
    try {
      const parsed = JSON.parse(raw);
      token = parsed?.access || parsed?.access_token || parsed?.token || raw;
    } catch {
      token = raw;
    }
  } catch {
    token = "";
  }
  let adminUnit: string | undefined;
  try {
    adminUnit = token ? String(jwtDecode<any>(token)?.admin_unit || "") || undefined : undefined;
  } catch {
    adminUnit = undefined;
  }
  return { token, adminUnit };
};

export interface StampSignatures {
  signature1?: File;
  signature2?: File;
}

// Convert a base64 / data-URI / URL image source into an image File.
// Returns null when the source is missing or is not an image.
async function toImageFile(
  src: string,
  name: string,
  token?: string,
): Promise<File | null> {
  if (!src || typeof src !== "string") return null;
  try {
    let res: Response;
    if (src.startsWith("data:")) {
      res = await fetch(src);
    } else {
      const url = src.startsWith("http")
        ? src
        : `${HRMS_BASE}${src.replace(/^\//, "")}`;
      res = await fetch(
        url,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );
    }
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith("image/")) return null;
    const ext = blob.type.split("/")[1] || "png";
    return new File([blob], `${name}.${ext}`, { type: blob.type });
  } catch (e) {
    console.error("Failed to load stamp signature image:", e);
    return null;
  }
}

// Collect candidate signature image sources from the read_stamp payload, in
// priority order (first -> signature1, second -> signature2).
function extractSignatureSources(payload: any): string[] {
  const sources: string[] = [];
  const push = (v: any) => {
    if (typeof v === "string" && v.trim()) sources.push(v.trim());
  };

  // First signature (bottom-center) candidates.
  push(payload?.signature1);
  push(payload?.signature_1);
  push(payload?.signature_one);
  push(payload?.first_signature);
  push(payload?.sign1);
  // Second signature (bottom-right) candidates.
  push(payload?.signature2);
  push(payload?.signature_2);
  push(payload?.signature_two);
  push(payload?.second_signature);
  push(payload?.sign2);
  // Generic single stamp/signature fallbacks.
  push(payload?.signature);
  push(payload?.stamp);
  push(payload?.stamp_image);
  push(payload?.image);
  // Array of signatures.
  if (Array.isArray(payload?.signatures)) {
    payload.signatures.forEach((s: any) =>
      push(typeof s === "string" ? s : s?.image ?? s?.signature ?? s?.url),
    );
  }

  // Preserve order, drop duplicates.
  return [...new Set(sources)];
}

// Fetch the office stamp and return it as signature1 (and a second image as
// signature2 when HRMS returns one). read_stamp streams the stamp image from
// DMS; older JSON answers with image sources are still understood.
export async function fetchServiceStampSignatures(
  token: string,
  adminUnitUuid: string | undefined = currentLogin().adminUnit,
  serviceName: string = RESEARCH_STAMP_SERVICE_NAME,
): Promise<StampSignatures> {
  if (!adminUnitUuid) throw new Error("No office in the login to find its stamp");
  const res = await fetch(
    `${HRMS_BASE}organization/service-stamp/read_stamp/?admin_unit_uuid=${encodeURIComponent(adminUnitUuid)}&service_name=${serviceName}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error("Failed to fetch service stamp");
  const blob = await res.blob();
  if (blob.type.startsWith("image/")) {
    const ext = blob.type.split("/")[1]?.replace("jpeg", "jpg") || "png";
    return { signature1: new File([blob], `office_stamp.${ext}`, { type: blob.type }) };
  }
  const data = JSON.parse(await blob.text());

  // The payload may be wrapped (e.g. { data: {...} } / { stamp: {...} }).
  const payload = data?.data ?? data?.stamp ?? data;
  const sources = extractSignatureSources(payload);


  const [s1, s2] = await Promise.all([
    sources[0] ? toImageFile(sources[0], "signature1", token) : Promise.resolve(null),
    sources[1] ? toImageFile(sources[1], "signature2", token) : Promise.resolve(null),
  ]);

  const result: StampSignatures = {};
  if (s1) result.signature1 = s1;
  if (s2) result.signature2 = s2;

  return result;
}

/**
 * Attach the office stamp to a registrar's outgoing paper: the backend
 * stamps the PDF on every page. Without a stamp in HRMS the paper is sent
 * unstamped (the error is only logged).
 */
export async function appendOfficeStamp(data: FormData, token?: string): Promise<boolean> {
  try {
    const login = currentLogin();
    const { signature1, signature2 } = await fetchServiceStampSignatures(token || login.token, login.adminUnit);
    if (signature1) data.append("signature1", signature1);
    if (signature2) data.append("signature2", signature2);
    return !!(signature1 || signature2);
  } catch (error) {
    console.warn("Office stamp not attached:", error);
    return false;
  }
}
